import React, { useEffect, useState, useRef } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

export default function PointCloudPanel({ projectId }) {
  const [clouds, setClouds] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // {id, stats, points, filename}
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/point-clouds`);
      setClouds(data || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  useEffect(() => { load(); }, [projectId]);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr("Pick a file first."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", "");
      await api.post(`/projects/${projectId}/point-clouds/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      fileRef.current.value = "";
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const showPreview = async (cloud) => {
    setErr(""); setPreview({ ...cloud, preview: "(loading…)" });
    try {
      const { data } = await api.get(`/projects/${projectId}/point-clouds/${cloud.id}/preview`);
      setPreview({ ...cloud, ...data });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
      setPreview(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this point cloud?")) return;
    setErr("");
    try {
      await api.delete(`/projects/${projectId}/point-clouds/${id}`);
      if (preview?.id === id) setPreview(null);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  // ── File 53: ICP scan registration + deviation analysis ──
  const [registration, setRegistration] = useState(null);
  const [deviation, setDeviation] = useState(null);

  const registerScan = async (cloud) => {
    setErr("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/point-cloud/register`,
        {
          filename: cloud.filename,
          point_count: cloud.stats?.points || 0,
          control_points: 4,
        });
      setRegistration(data);
      setDeviation(null);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  const analyzeDeviation = async (cloud) => {
    setErr("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/point-cloud/deviation`,
        { filename: cloud.filename, point_count: cloud.stats?.points || 0 });
      setDeviation(data);
      setRegistration(null);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  return (
    <div data-testid="pointcloud-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...label, marginBottom: 2 }}>Point Cloud / Laser-Scan</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Upload existing-plant scans (E57 · LAS · LAZ · PTS · PLY · XYZ). XYZ files are parsed
            for point count + bbox; binary formats are stored verbatim.
          </div>
        </div>
        <form onSubmit={upload} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input ref={fileRef} type="file" data-testid="pc-file"
            accept=".e57,.las,.laz,.pts,.ptx,.ply,.xyz,.txt"
            style={{ fontSize: 11, color: "var(--pf-fg)" }} />
          <button type="submit" data-testid="pc-upload" style={btn} disabled={busy}>
            {busy ? "Uploading…" : "Upload"}
          </button>
        </form>
      </div>

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      <div style={card}>
        <div style={{ ...label, marginBottom: 6 }}>Uploaded Scans ({clouds.length})</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "var(--pf-surface-2)" }}>
                {["File", "Format", "Size", "Points", "BBox (m)", "Uploaded", "Action"].map((h) =>
                  <th key={h} style={{ padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {clouds.map((c) => (
                <tr key={c.id} style={{ borderBottom: "1px solid var(--pf-border)" }} data-testid={`pc-row-${c.id}`}>
                  <td style={{ padding: 6, fontWeight: 700, color: "#F26B1F" }}>{c.filename}</td>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>{c.ext}</td>
                  <td style={{ padding: 6 }}>{(c.size_bytes / 1024).toFixed(1)} KB</td>
                  <td style={{ padding: 6 }}>{c.stats?.points ?? "—"}</td>
                  <td style={{ padding: 6, fontSize: 10 }}>
                    {c.stats?.bbox ? `${c.stats.bbox.size.x} × ${c.stats.bbox.size.y} × ${c.stats.bbox.size.z}` : "—"}
                  </td>
                  <td style={{ padding: 6, fontSize: 10 }}>{c.uploaded_at?.slice(0, 10)}</td>
                  <td style={{ padding: 6 }}>
                    <button data-testid={`pc-preview-${c.id}`} style={{ ...btnGhost, padding: "3px 8px", fontSize: 10, marginRight: 4 }}
                      onClick={() => showPreview(c)} disabled={!c.stats?.points}>Preview</button>
                    <button data-testid={`pc-register-${c.id}`} style={{ ...btnGhost, padding: "3px 8px", fontSize: 10, marginRight: 4, color: "#16a34a" }}
                      onClick={() => registerScan(c)}>Register</button>
                    <button data-testid={`pc-deviation-${c.id}`} style={{ ...btnGhost, padding: "3px 8px", fontSize: 10, marginRight: 4, color: "#FFB000" }}
                      onClick={() => analyzeDeviation(c)}>Deviation</button>
                    <button data-testid={`pc-delete-${c.id}`} style={{ ...btnGhost, padding: "3px 8px", fontSize: 10, color: "#ef4444" }}
                      onClick={() => remove(c.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {clouds.length === 0 && (
                <tr><td colSpan="7" style={{ padding: 16, textAlign: "center", color: "var(--pf-muted)" }}>
                  No point clouds yet — upload an E57 or XYZ file to get started.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {registration && (
        <div data-testid="pc-registration-result" style={{
          ...card, background: "rgba(22,163,74,0.08)",
          border: "1px solid rgba(22,163,74,0.4)",
        }}>
          <div style={{ ...label, marginBottom: 6 }}>
            ICP Registration —{" "}
            <span style={{ color: registration.status?.startsWith("REGIST") ? "#16a34a" : "#FFB000" }}>
              {registration.status}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, fontSize: 11 }}>
            <div><span style={{ color: "var(--pf-muted)" }}>Reg ID:</span>{" "}
              <span style={{ fontFamily: "monospace" }}>{registration.registration_id}</span></div>
            <div><span style={{ color: "var(--pf-muted)" }}>Fitness:</span>{" "}
              <strong style={{ color: "#16a34a" }}>{(registration.transform.fitness_score * 100).toFixed(1)}%</strong></div>
            <div><span style={{ color: "var(--pf-muted)" }}>RMS error:</span>{" "}
              <strong>{registration.transform.rms_error_mm} mm</strong></div>
            <div><span style={{ color: "var(--pf-muted)" }}>Control pts:</span>{" "}
              {registration.control_points_used}</div>
          </div>
          <div style={{ marginTop: 6, fontSize: 10, color: "var(--pf-muted)" }}>
            {registration.method} · {registration.tools}
          </div>
        </div>
      )}

      {deviation && (
        <div data-testid="pc-deviation-result" style={{
          ...card, background: "rgba(255,176,0,0.08)",
          border: "1px solid rgba(255,176,0,0.4)",
        }}>
          <div style={{ ...label, marginBottom: 6 }}>
            Deviation Analysis ({deviation.summary.total_elements_checked} elements) —{" "}
            <span style={{
              color: deviation.summary.compliance_pct >= 90 ? "#16a34a"
                   : deviation.summary.compliance_pct >= 70 ? "#FFB000"
                   : "#dc2626",
            }}>
              {deviation.summary.compliance_pct}% compliant
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, fontSize: 11 }}>
            <div><span style={{ color: "var(--pf-muted)" }}>Within tol:</span>{" "}
              <strong style={{ color: "#16a34a" }}>{deviation.summary.within_tolerance}</strong></div>
            <div><span style={{ color: "var(--pf-muted)" }}>Out of tol:</span>{" "}
              <strong style={{ color: "#dc2626" }}>{deviation.summary.out_of_tolerance}</strong></div>
            <div><span style={{ color: "var(--pf-muted)" }}>Max dev:</span>{" "}
              <strong>{deviation.summary.max_deviation_mm} mm</strong></div>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 10, borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "var(--pf-surface-2)" }}>
              {["Element", "Type", "Deviation", "Tolerance", "Status"].map((h) =>
                <th key={h} style={{ padding: 4, textAlign: "left", color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(deviation.deviations || []).map((d, i) => (
                <tr key={i} data-testid={`pc-deviation-row-${i}`} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                  <td style={{ padding: 4 }}><strong style={{ color: "#F26B1F" }}>{d.element}</strong></td>
                  <td style={{ padding: 4 }}>{d.type}</td>
                  <td style={{ padding: 4 }}>{d.deviation_mm} mm</td>
                  <td style={{ padding: 4 }}>{d.tolerance_mm} mm</td>
                  <td style={{ padding: 4, color: d.status.includes("WITHIN") ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                    {d.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div style={card} data-testid="pc-preview-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ ...label, marginBottom: 0 }}>Preview · {preview.filename}</div>
            <button style={btnGhost} onClick={() => setPreview(null)}>Close</button>
          </div>
          {preview.stats && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--pf-muted)" }}>
              {preview.stats.points} points · bbox {preview.stats.bbox?.size?.x ?? "?"} × {preview.stats.bbox?.size?.y ?? "?"} × {preview.stats.bbox?.size?.z ?? "?"} m
            </div>
          )}
          {preview.note && (
            <div style={{ marginTop: 8, padding: 10, background: "rgba(245,158,11,.10)", border: "1px solid rgba(245,158,11,.4)", borderRadius: 4, fontSize: 11, color: "#f59e0b" }}>
              {preview.note}
            </div>
          )}
          {typeof preview.preview === "string" && preview.preview.length > 0 && (
            <pre style={{ marginTop: 8, padding: 10, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 10, fontFamily: "monospace",
                          maxHeight: 240, overflowY: "auto", color: "var(--pf-fg)" }}>
              {preview.preview.split("\n").slice(0, 200).join("\n")}
            </pre>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: "var(--pf-muted)" }}>
            Sub-sampled to ≤ 20 000 points for browser preview. Full Three.js point-cloud renderer (Potree.js) is a future enhancement.
          </div>
        </div>
      )}
    </div>
  );
}
